/**
 * Super-Admin auth provider — email magic-link через Supabase Auth.
 *
 * Отличие от tenant-staff SupabaseAuthProvider (PIN-flow):
 *   • НЕТ PIN screen
 *   • НЕТ tenant_id (super_admin кросс-tenant)
 *   • НЕТ role check tenant-ролей (waiter/cashier/...)
 *   • Auth через signInWithOtp({email}) → magic-link на почту → callback
 *
 * Сессия живёт в localStorage по дефолту Supabase. После клика по magic-link
 * GoTrue verify редиректит с implicit-грантом (#access_token=…); singleton на
 * flowType:"pkce" его НЕ потребляет автоматически — поэтому ловим хэш в bootstrap
 * и явно зовём setSession (см. блок «0. Magic-link callback» ниже).
 *
 * Role гейт делает SuperAdminGate — здесь только session state machine.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { getSupabaseClient } from "@cafe-os/db";

type AuthStatus =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "anonymous" }
  | { kind: "magicLinkSent"; email: string }
  | { kind: "authenticated"; session: Session; user: User }
  | { kind: "error"; message: string };

interface SuperAdminAuthValue {
  status: AuthStatus;
  signInWithEmail: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<SuperAdminAuthValue | null>(null);

export function SuperAdminAuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>({ kind: "loading" });

  useEffect(() => {
    // Хэш читаем ДО getSupabaseClient(): init клиента (detectSessionInUrl с
    // flowType:"pkce") трогает URL и стирает implicit-фрагмент раньше, чем мы
    // успеем его прочитать, если getSupabaseClient() вызвать первым. (E2E
    // показал: чтение после getSupabaseClient() → хэш уже пуст → не потребляется.)
    const initialHash = typeof window !== "undefined" ? window.location.hash : "";
    const sb = getSupabaseClient();
    let cancelled = false;

    const bootstrapFromStorage = () => {
      sb.auth.getSession().then(({ data }) => {
        if (cancelled) return;
        if (data.session) {
          setStatus({
            kind: "authenticated",
            session: data.session,
            user: data.session.user,
          });
        } else {
          setStatus({ kind: "anonymous" });
        }
      });
    };

    // 0. Magic-link callback: GoTrue verify редиректит на /platform/ с implicit-
    //    грантом (#access_token=…&refresh_token=…). Singleton-клиент на
    //    flowType:"pkce" авто-потребляет только ?code=, поэтому detectSessionInUrl
    //    ИГНОРИРУЕТ implicit-хэш → getSession() вернул бы null и super-admin
    //    остался бы на экране логина. Явно скармливаем токены в setSession;
    //    onAuthStateChange ниже поймает SIGNED_IN. (Тот же баг, что в auth-app
    //    для staff, фикс 65f1dea.)
    const hash = initialHash;
    if (hash.includes("access_token=")) {
      const params = new URLSearchParams(hash.replace(/^#/, ""));
      const accessToken = params.get("access_token");
      const refreshToken = params.get("refresh_token");
      try {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      } catch {
        /* ignore */
      }
      if (accessToken && refreshToken) {
        // status остаётся "loading" — onAuthStateChange выставит authenticated.
        void sb.auth
          .setSession({ access_token: accessToken, refresh_token: refreshToken })
          .then(({ error }) => {
            if (error) {
              console.error("[SuperAdminAuth] magic-link setSession failed:", error.message);
              if (!cancelled) bootstrapFromStorage();
            }
          })
          .catch((e) => {
            console.error("[SuperAdminAuth] magic-link setSession threw:", e);
            if (!cancelled) bootstrapFromStorage();
          });
      } else {
        // #error=… (например otp_expired) — токенов нет, идём в обычный bootstrap.
        bootstrapFromStorage();
      }
    } else {
      // 1. Обычный bootstrap: читаем существующую session из localStorage.
      bootstrapFromStorage();
    }

    // 2. Listener — реагирует на magic-link callback, refresh, sign-out
    const { data: sub } = sb.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === "SIGNED_IN" && session) {
        setStatus({ kind: "authenticated", session, user: session.user });
      } else if (event === "SIGNED_OUT") {
        setStatus({ kind: "anonymous" });
      } else if (event === "TOKEN_REFRESHED" && session) {
        setStatus({ kind: "authenticated", session, user: session.user });
      } else if (event === "USER_UPDATED" && session) {
        // Trigger set_super_admin_role обновил app_metadata —
        // обновляем status чтобы Gate перечитал JWT.
        setStatus({ kind: "authenticated", session, user: session.user });
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const signInWithEmail = useCallback(async (email: string) => {
    setStatus({ kind: "loading" });
    const sb = getSupabaseClient();
    // Маршрутизируем через Edge Function `superAdminRequestMagicLink`:
    // она проверяет email в super_admin_allowlist + rate-limit'ит по IP и
    // шлёт ссылку ТОЛЬКО allowlisted адресам, но ВСЕГДА отвечает ok:true
    // (anti-enumeration). Раньше прямой signInWithOtp слал magic link на
    // любую почту → enumeration + брендовый спам.
    const redirectTo =
      typeof window !== "undefined" ? window.location.origin + "/platform/" : undefined;
    const { data, error } = await sb.functions.invoke<{ ok: boolean }>(
      "superAdminRequestMagicLink",
      { body: { email, redirectTo } },
    );
    if (error) {
      // Rate-limit (429) и прочие ошибки приходят как FunctionsHttpError.
      let message = "Не удалось отправить ссылку. Попробуйте позже.";
      try {
        const ctx = (error as { context?: Response }).context;
        if (ctx && typeof ctx.json === "function") {
          const payload = await ctx.json();
          if (payload?.error?.code === "RATE_LIMITED") {
            message = "Слишком много попыток. Подождите минуту и попробуйте снова.";
          } else if (payload?.error?.message) {
            message = payload.error.message;
          }
        }
      } catch {
        // keep default message
      }
      setStatus({ kind: "error", message });
      return;
    }
    if (!data?.ok) {
      setStatus({ kind: "error", message: "Не удалось отправить ссылку. Попробуйте позже." });
      return;
    }
    // Одинаковый UX независимо от того, в allowlist ли email (anti-enumeration):
    // «если адрес разрешён — ссылка отправлена».
    setStatus({ kind: "magicLinkSent", email });
  }, []);

  const signOut = useCallback(async () => {
    const sb = getSupabaseClient();
    await sb.auth.signOut();
    setStatus({ kind: "anonymous" });
  }, []);

  return (
    <Ctx.Provider value={{ status, signInWithEmail, signOut }}>
      {children}
    </Ctx.Provider>
  );
}

export function useSuperAdminAuth(): SuperAdminAuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSuperAdminAuth must be inside <SuperAdminAuthProvider>");
  return v;
}
